import type { ReactNode } from 'react';
import { BOX_MARGIN, boxZoomStyle } from './BoxOverlay';
import { useImageSizes } from './imageSize';
import type { ListingCrop } from './listingCropsRun';
import { useLanguage } from '../../contexts/LanguageContext';
import { Band } from './Band';
import { PICKED } from './pickedLook';

// THE PART ITSELF, cut out of the photographs it was found in — a row of them, beside the
// model of it.
//
// The crop and not the photograph. A listing picture is a chair, and what is being compared
// to the mesh is a fourteen-millimetre button somewhere in it: shown whole, the thing you
// are looking for is a smudge you have to find first, four times over. Cut to its box it is
// the same object the viewer is drawing, at about the same size, in real light.
//
// CUT BY TRANSFORM, not by cropping the file. The image is moved and scaled inside a frame
// that clips it — the same arithmetic the zoom uses (boxZoomStyle), so a tile and the box
// drawn on the full picture cannot disagree about where the part is.
//
// THE TILE IS THE SHAPE OF THE BOX, which is the fix for tiles that showed a whole chair.
// A square frame can only fit a box by its LONGER side (that is what boxZoomStyle does with
// one uniform scale), so a detection 18% wide and 85% tall — a chair's rear leg seen from the
// side — was scaled by 1.1 and the remaining width filled with the rest of the chair. It was
// cropping; a sliver simply cannot fill a square.
//
// Matching the frame to the box needs the box's TRUE aspect, which four fractions of a
// picture cannot give on their own: 18% of a wide photograph and 18% of a tall one are
// different shapes. So the picture's own aspect is measured once, on load, and the frame is
// built from the two together. Then one uniform scale fits both axes at once and the tile IS
// the crop.
//
// THE MANUAL'S OWN DRAWING comes in the same list, marked, and leads the row — see the schema
// flag. It answers a different question: the drawing is what the part is SUPPOSED to look
// like, the crops are what it turned out to look like, and the dashed border is what says
// which is which.
export type StripCrop = ListingCrop & { schema?: boolean };

// How tall every tile is. Their widths differ, because their boxes do.
const TILE = 64;

// And how wide one may get, either way. A box 8% tall and 90% wide would otherwise be a
// 720-pixel tile that pushes every other crop off the row; a box 90% tall and 8% wide would
// be six pixels of nothing. Past these the fit is imperfect again, which is the right trade:
// legible beats exact at 64 pixels.
const MIN_W = 26;
const MAX_W = 132;

// The natural aspect (width / height) of every picture this strip has drawn, by URL — off
// the measurement useImageSizes already keeps, which is the same question asked in pixels.
function useAspects(urls: string[]): Map<string, number> {
  const sizes = useImageSizes(urls);
  const out = new Map<string, number>();
  for (const [url, { w, h }] of sizes) out.set(url, w / h);
  return out;
}

// How wide a crop's tile is: the box's own shape, in the picture's own proportions. Square
// until the picture has loaded, which is the same answer the strip used to give always.
function widthOf(
  crop: StripCrop, aspect: number | undefined, height: number, margin: number,
): number {
  if (crop.schema || !aspect) return height;
  // THE PADDED box, which is what is actually drawn: boxZoomStyle leaves BOX_MARGIN around
  // it, and shaping the frame to the bare box would leave the two arithmetics disagreeing by
  // exactly that margin.
  const [x0, y0, x1, y1] = crop.bbox;
  const w = Math.min(1, Math.max(1e-4, x1 - x0) + margin * 2);
  const h = Math.min(1, Math.max(1e-4, y1 - y0) + margin * 2);
  const shape = (w * aspect) / h;
  const k = height / TILE;
  return Math.round(Math.min(MAX_W * k, Math.max(MIN_W * k, height * shape)));
}

// WHETHER THE TILE WAS WIDENED past the box's shape — a tall thin box held at the minimum
// width. Fitted by width, that box would run off the top and bottom of its frame; fitted by
// height it is whole, with a little page either side. (A box NARROWED to the maximum width
// needs nothing: fitted by width it is whole, with a little page above and below.)
function widened(crop: StripCrop, aspect: number | undefined, height: number, margin: number) {
  if (crop.schema || !aspect) return false;
  const [x0, y0, x1, y1] = crop.bbox;
  const w = Math.min(1, Math.max(1e-4, x1 - x0) + margin * 2);
  const h = Math.min(1, Math.max(1e-4, y1 - y0) + margin * 2);
  return widthOf(crop, aspect, height, margin) - height * ((w * aspect) / h) > 1;
}

// ONE CROP, CUT OUT BY TRANSFORM and framed to its box. Exported because the parts table
// draws a piece of hardware's page crops the same way — the crop, not the page it is on — and
// the generator's inputs are these tiles too: a second copy of this arithmetic is where one of
// them quietly starts showing the page around the part again.
//
// `on`, where the tile is a choice: ringed when chosen, dimmed when not. `dim` for a crop
// somebody struck through in the table.
//
// `exact` frames the box and nothing else — what the picture's own green rectangle encloses.
// BOX_MARGIN is a share of the whole PICTURE, a thin frame round a chair leg and twice the
// size of a washer on a page of the manual; and the fourteen-times cap on magnification would
// leave that washer short of its frame too. So neither applies.
export function CropTile({ crop, label, onOpen, height = TILE, on, dim, exact, children }: {
  crop: StripCrop;
  label: string;
  onOpen: () => void;
  height?: number;
  on?: boolean;
  dim?: boolean;
  exact?: boolean;
  children?: ReactNode;
}) {
  const aspect = useAspects(crop.schema ? [] : [crop.url]).get(crop.url);
  const margin = exact ? 0 : BOX_MARGIN;
  const width = widthOf(crop, aspect, height, margin);
  const ring = on === undefined
    ? (crop.schema
      ? 'border-dashed border-slate-400 bg-white'
      : 'border-slate-300 bg-white')
    : on
      ? PICKED
      : 'border-2 border-slate-300 bg-white opacity-50';
  return (
    <button
      type="button"
      onClick={onOpen}
      title={label}
      aria-label={label}
      aria-pressed={on}
      style={{ width, height }}
      className={`relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg border transition-colors hover:border-blue-500 ${
        on === undefined ? 'cursor-zoom-in' : 'cursor-pointer'
      } ${ring} ${dim ? 'opacity-40' : ''}`}
    >
      {/* THE DRAWING WHOLE, and so laid out the other way: contained rather than moved
          and scaled. There is no box to fill the frame with, and a drawing taller than it
          is wide would hang out of the tile under the crops' rule, which sets the image's
          WIDTH and lets the height fall where it falls. */}
      {crop.schema ? (
        <img
          src={crop.url}
          alt=""
          loading="lazy"
          className="max-h-full max-w-full object-contain p-1"
        />
      ) : (
        /* Centred in the frame, so the transform — which moves the box to the IMAGE's
           centre — lands it in the middle of the tile. Laid out at its natural shape and
           full width: an object-cover crop would move the pixels first and leave the box
           pointing somewhere else. */
        <img
          src={crop.url}
          alt=""
          loading="lazy"
          className="w-full max-w-none"
          // Fitted by WIDTH, which is exact here: the frame above was built to this box's
          // own shape, so one uniform scale fills both axes. See boxZoomStyle.
          style={widened(crop, aspect, height, margin)
            ? boxZoomStyle(crop.bbox, margin, exact ? Infinity : 14, 'height',
              width / height / (aspect ?? 1))
            : boxZoomStyle(crop.bbox, margin, exact ? Infinity : 14, 'width')}
        />
      )}
      {children}
    </button>
  );
}

export function CropStrip({ crops, alt, onOpen }: {
  crops: StripCrop[];
  alt: string;
  onOpen: (crop: StripCrop) => void;
}) {
  const { t } = useLanguage();
  if (!crops.length) return null;
  const drawing = crops.find((c) => c.schema);
  const shots = crops.filter((c) => !c.schema);

  const tile = (crop: StripCrop, key: string) => (
    <CropTile
      key={key}
      crop={crop}
      label={crop.schema ? `${t('notice.catalog.schematic')} — ${alt}` : alt}
      onOpen={() => onOpen(crop)}
    />
  );

  // ONE ROW, DRAWING FIRST. It had a line of its own above the photographs, which said
  // "these are two different kinds of picture" at the cost of a second row in the one part
  // of this modal that is fighting for height — and the dashed border says that already.
  // First rather than anywhere else because it is what the part is SUPPOSED to look like,
  // and the photographs are read against it.
  //
  // Scrolls rather than wraps. A part found a dozen times is a dozen tiles, and a second row
  // of them would push the mesh down the screen — which is the one thing here that must not
  // move to make room for something beside it.
  //
  // CENTRED BY AUTO MARGINS on a shrink-wrapped inner row, not by justify-center on the
  // scroller. A centred flex container that overflows spills out BOTH sides and the left
  // spill cannot be scrolled back to: the first tiles are simply gone. With `w-max mx-auto`
  // the margins do nothing once the row is wider than the box, so it starts at the left edge
  // and scrolls, and still centres when it fits.
  return (
    <Band title={t('notice.3d.bandReference')}>
      <div className="w-full overflow-x-auto pb-1">
        <div className="mx-auto flex w-max items-center gap-1.5">
          {drawing && tile(drawing, 'schema')}
          {shots.map((crop, i) => tile(crop, `${crop.url}-${i}`))}
        </div>
      </div>
    </Band>
  );
}
